import React, { useState, useEffect } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useMsal } from '@azure/msal-react';
import { hasRoleAccess } from './utils/roles'; // Import role-checking helper
import 'primereact/resources/themes/saga-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import 'primeflex/primeflex.css';
import './GlobalStyles.css';

const ShippingItems = ({ userRole }) => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(100);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(true);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
  const navigate = useNavigate();
  const { instance, accounts } = useMsal();

  useEffect(() => {
    console.log("User Role in ShippingItems:", userRole); // Debug Log
    if (accounts.length > 0) {
      fetchData(page, limit, sortField, sortOrder, searchQuery);
    }
  }, [accounts, page, limit, sortField, sortOrder]);

  const fetchData = async (page, limit, sortField, sortOrder, searchQuery) => {
    setLoading(true);
    try {
      const tokenResponse = await instance.acquireTokenSilent({
        scopes: ["https://database.windows.net/.default"],
        account: accounts[0]
      });
      await fetchDataWithToken(
        tokenResponse.accessToken,
        page,
        limit,
        sortField,
        sortOrder,
        searchQuery
      );
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDataWithToken = async (token, page, limit, sortField, sortOrder, searchQuery) => {
    try {
      const response = await axios.get(`${apiBaseUrl}/api/shipping-items`, {
        params: { page: page + 1, limit, sortField, sortOrder, searchQuery },
        headers: { Authorization: `Bearer ${token}` }
      });
      const fetchedData = response.data.items || [];
      setData(fetchedData);
      setTotalItems(response.data.totalItems || 0);
      setFilteredData(fetchedData);
    } catch (error) {
      console.error('Error fetching data with token:', error);
    }
  };

  const onPageChange = (event) => {
    setPage(event.page);
    setLimit(event.rows);
  };

  const onSort = (event) => {
    setSortField(event.sortField);
    setSortOrder(event.sortOrder);
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const handleSearch = () => {
    fetchData(page, limit, sortField, sortOrder, searchQuery);
  };

  const clearSearch = () => {
    setSearchQuery('');
    fetchData(page, limit, sortField, sortOrder, '');
  };

  // Trigger search when Enter key is pressed in the search bar
  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleSearch();
    }
  };

  const viewItem = (shippingID) => {
    navigate(`/shipping-item/${shippingID}`);
  };

  const editItem = (shippingID) => {
    navigate(`/edit-shipping-item/${shippingID}`);
  };

  const actionBodyTemplate = (rowData) => {
    const canEdit = hasRoleAccess(userRole, 'shipping-update');
    return (
      <div className="action-buttons">
        <button
          onClick={(e) => { e.stopPropagation(); viewItem(rowData.ShippingID); }}
          className="view-item-button button"
        >
          View
        </button>
        {canEdit && (
          <button
            onClick={(e) => { e.stopPropagation(); editItem(rowData.ShippingID); }}
            className="edit-item-button button hide-on-mobile"
          >
            Edit
          </button>
        )}
      </div>
    );
  };

  const formatDate = (dateStr) => dateStr.split('T')[0];

  return (
    <div className="page-container">
      <div className="header-container">
        <Link to="/shipping">
          <button className="back-button button">Back</button>
        </Link>
        <h1>Shipping Items</h1>
      </div>
      <div className="search-container">
        <input
          type="text"
          className="search-box"
          placeholder="Search..."
          value={searchQuery}
          onChange={handleSearchChange}
          onKeyDown={handleKeyDown}
        />
        <button onClick={handleSearch} className="search-button button">
          Search
        </button>
        <button onClick={clearSearch} className="clear-button button">
          Clear
        </button>
      </div>
      <div className="table-container">
        <DataTable
          value={filteredData}
          paginator
          rows={limit}
          totalRecords={totalItems}
          loading={loading}
          lazy
          first={page * limit}
          onPage={onPageChange}
          sortField={sortField}
          sortOrder={sortOrder}
          onSort={onSort}
          onRowClick={(e) => viewItem(e.data.ShippingID)}
        >
          {/* Always visible columns */}
          <Column field="ShippingID" header="Shipping ID" sortable />
          <Column field="ProjectNumber" header="Project Number" sortable />

          {/* Hidden on mobile */}
          <Column
            field="QtyInLot"
            header="Quantity in Lot"
            headerClassName="hide-on-mobile"
            bodyClassName="hide-on-mobile"
            sortable
          />
          <Column
            field="ProjectManager"
            header="Project Manager"
            headerClassName="hide-on-mobile"
            bodyClassName="hide-on-mobile"
            sortable
          />
          {/* Updating Company to Client as per the Scott's request */}
          <Column
            field="Company"
            header="Client"
            headerClassName="hide-on-mobile"
            bodyClassName="hide-on-mobile"
            sortable
          />
          <Column
            field="Rcvd_Date"
            header="Received Date"
            body={(rowData) => formatDate(rowData.Rcvd_Date)}
            headerClassName="hide-on-mobile"
            bodyClassName="hide-on-mobile"
            sortable
          />
          <Column
            field="Receiver"
            header="Receiver"
            headerClassName="hide-on-mobile"
            bodyClassName="hide-on-mobile"
            sortable
          />

          {/* Actions column */}
          <Column body={actionBodyTemplate} header="Actions" />
        </DataTable>
      </div>
    </div>
  );
};

export default ShippingItems;
